import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaTwitter, FaEnvelope, FaHeart } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const FooterContainer = styled.footer<{ $isDarkMode: boolean }>`
  background: ${props => props.$isDarkMode 
    ? 'linear-gradient(135deg, rgba(8, 8, 20, 1), rgba(139, 92, 246, 0.25))'
    : 'linear-gradient(135deg, rgba(226, 232, 240, 0.42), rgba(139, 92, 246, 0.2))'
  };
  backdrop-filter: blur(10px);
  border-top: ${props => props.$isDarkMode 
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(139, 92, 246, 0.1)'
  };
  padding: 4rem 2rem 2rem 2rem;

  @media (max-width: 768px) {
    padding: 3rem 1rem 1.5rem 1rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 2fr 1fr 1fr;
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const FooterTitle = styled.h4<{ $isDarkMode: boolean }>`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#1e293b'};
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }
`;

const FooterText = styled.p`
  color: #94a3b8;
  line-height: 1.6;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

const FooterLink = styled.a`
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    color: #8b5cf6;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 0.75rem;
  }
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 50%;
  color: #8b5cf6;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    margin-bottom: 0.75rem;
  }
`;

const Logo = styled.img`
  width: 35px;
  height: 35px;
  filter: drop-shadow(0 4px 8px rgba(139, 92, 246, 0.3));

  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
  }
`;

const LogoText = styled(motion.span)`
  font-size: 1.3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #a0a0a0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    font-size: 0.85rem;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const HeartIcon = styled(motion.div)`
  color: #ec4899;
  display: inline-block;

  @media (max-width: 768px) {
    margin: 0.25rem 0;
  }
`;

const Footer: React.FC = () => {
  const { isDarkMode } = useTheme();
  const logoVariants = {
    initial: { scale: 1 },
    hover: { 
      scale: 1.1,
      rotate: [0, -10, 10, 0],
      transition: { duration: 0.6 }
    }
  };

  const letterVariants = {
    initial: { y: 0 },
    hover: { 
      y: [-3, 3, -3],
      transition: { 
        duration: 0.8,
        repeat: Infinity,
        repeatType: "reverse" as const
      }
    }
  };

  const heartVariants = {
    initial: { scale: 1 },
    animate: { 
      scale: [1, 1.2, 1],
      transition: { 
        duration: 1.5,
        repeat: Infinity,
        repeatType: "reverse" as const
      }
    }
  };

  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to section if state is present (on homepage)
  React.useEffect(() => {
    if (location.pathname === '/' && location.state && location.state.sectionId) {
      const el = document.getElementById(location.state.sectionId);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const handleNav = (sectionId: string) => {
    if (location.pathname === '/') {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { sectionId } });
    }
  };

  return (
    <FooterContainer $isDarkMode={isDarkMode}>
      <FooterContent>
        <FooterSection>
          <LogoContainer
            variants={logoVariants}
            initial="initial"
            whileHover="hover"
          >
            <Logo src="/logo-pink.svg" alt="Briddy - Full-Stack Developer Logo" />
            <LogoText>
              <motion.span variants={letterVariants} initial="initial" whileHover="hover">B</motion.span>
              <motion.span variants={letterVariants} initial="initial" whileHover="hover">r</motion.span>
              <motion.span variants={letterVariants} initial="initial" whileHover="hover">i</motion.span>
              <motion.span variants={letterVariants} initial="initial" whileHover="hover">d</motion.span>
              <motion.span variants={letterVariants} initial="initial" whileHover="hover">d</motion.span>
              <motion.span variants={letterVariants} initial="initial" whileHover="hover">y</motion.span>
            </LogoText>
          </LogoContainer>
          <FooterText>
            Full-stack developer passionate about creating innovative digital solutions. 
            Let's build something amazing together!
          </FooterText>
          <SocialLinks>
            <SocialIcon
              href="https://github.com/briddytech"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaGithub />
            </SocialIcon>
            <SocialIcon
              href="https://linkedin.com/in/briddytech"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaLinkedin />
            </SocialIcon>
            <SocialIcon
              href="https://twitter.com/briddytech"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaTwitter />
            </SocialIcon>
            <SocialIcon
              href="/#contact"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaEnvelope />
            </SocialIcon>
          </SocialLinks>
        </FooterSection>

        <FooterSection>
          <FooterTitle $isDarkMode={isDarkMode}>Quick Links</FooterTitle>
          <FooterLink href="#about" onClick={e => { e.preventDefault(); handleNav('about'); }}>About Me</FooterLink>
          <FooterLink href="#projects" onClick={e => { e.preventDefault(); handleNav('projects'); }}>Projects</FooterLink>
          <FooterLink href="#testimonials" onClick={e => { e.preventDefault(); handleNav('testimonials'); }}>Testimonials</FooterLink>
          <FooterLink href="#contact" onClick={e => { e.preventDefault(); handleNav('contact'); }}>Contact</FooterLink>
        </FooterSection>

        <FooterSection>
          <FooterTitle $isDarkMode={isDarkMode}>Services</FooterTitle>
          <FooterLink href="#projects" onClick={e => { e.preventDefault(); handleNav('projects'); }}>Web Development</FooterLink>
          <FooterLink href="#projects" onClick={e => { e.preventDefault(); handleNav('projects'); }}>Mobile Apps</FooterLink>
          <FooterLink href="#projects" onClick={e => { e.preventDefault(); handleNav('projects'); }}>UI/UX Design</FooterLink>
          <FooterLink href="https://cal.com/briddy.tech" target="_blank" rel="noopener noreferrer">Consulting</FooterLink>
        </FooterSection>

        <FooterSection>
          <FooterTitle $isDarkMode={isDarkMode}>Legal</FooterTitle>
          <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
          <FooterLink href="/terms-of-service">Terms of Service</FooterLink>
        </FooterSection>
      </FooterContent>

      <Copyright>
        <span>© 2025 Briddy. Made with</span>
        <HeartIcon
          variants={heartVariants}
          initial="initial"
          animate="animate"
        >
          <FaHeart />
        </HeartIcon>
        <span>for the web.</span>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer; 